/**
 * Numbered Points — carousel sync (images + content), arrows + dot tabs,
 * mobile: swipe horizontal (arrows hidden via CSS ≤767px).
 */
import { isBlockOnPage } from "../../src/js/modules/block-present.js";

(() => {
  if (!isBlockOnPage("numbered-points")) {
    return;
  }

  if (document.body?.classList.contains("wp-admin")) {
    return;
  }

  const MOBILE_SWIPE_MQ = "(max-width: 767px)";
  const SWIPE_THRESHOLD_PX = 50;

  function isMobileSwipeWidth() {
    return window.matchMedia(MOBILE_SWIPE_MQ).matches;
  }

  function getCurrentIndex(block) {
    const ds = parseInt(block.dataset.currentSlide, 10);
    if (!Number.isNaN(ds)) return ds;
    const active = block.querySelector(".numbered-points__pagination-dot.is-active");
    if (active) {
      const t = parseInt(active.getAttribute("data-slide-to"), 10);
      if (!Number.isNaN(t)) return t;
    }
    return 0;
  }

  function syncContentViewportHeight(block, animate = true) {
    const viewport = block.querySelector(".numbered-points__viewport--content");
    const slides = block.querySelectorAll(".numbered-points__slide--content");
    const slide = slides[getCurrentIndex(block)];
    if (!viewport || !slide) return;

    if (!animate) {
      viewport.style.transition = "none";
    }

    viewport.style.height = `${slide.offsetHeight}px`;

    if (!animate) {
      void viewport.offsetHeight;
      viewport.style.transition = "";
    }
  }

  function goToSlide(block, index) {
    const count = parseInt(block.getAttribute("data-slide-count"), 10);
    if (!Number.isFinite(count) || count < 1) return;

    const i = Math.max(0, Math.min(index, count - 1));
    block.dataset.currentSlide = String(i);
    const pct = (-100 * i) / count;

    const trackImages = block.querySelector(
      '[data-carousel-track="images"]',
    );
    const trackContent = block.querySelector(
      '[data-carousel-track="content"]',
    );

    if (trackImages) {
      trackImages.style.transform = `translateX(${pct}%)`;
    }
    if (trackContent) {
      trackContent.style.transform = `translateX(${pct}%)`;
    }

    syncContentViewportHeight(block);

    block.querySelectorAll(".numbered-points__pagination-dot").forEach((btn, idx) => {
      const on = idx === i;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
      btn.setAttribute("tabindex", on ? "0" : "-1");
    });

    block.querySelectorAll(".numbered-points__slide--content").forEach(
      (slide, idx) => {
        slide.setAttribute("aria-hidden", idx === i ? "false" : "true");
      },
    );
  }

  function initMobileSwipe(block) {
    const swipeEl = block.querySelector("[data-numbered-points-swipe]");
    if (!swipeEl) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    swipeEl.addEventListener(
      "touchstart",
      (e) => {
        if (!isMobileSwipeWidth() || e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      },
      { passive: true },
    );

    swipeEl.addEventListener(
      "touchcancel",
      () => {
        tracking = false;
      },
      { passive: true },
    );

    swipeEl.addEventListener(
      "touchend",
      (e) => {
        if (!tracking || !isMobileSwipeWidth()) {
          tracking = false;
          return;
        }
        tracking = false;

        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        if (Math.abs(dx) <= Math.abs(dy)) return;
        if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;

        const c = parseInt(block.getAttribute("data-slide-count"), 10);
        if (!Number.isFinite(c) || c < 2) return;

        const cur = getCurrentIndex(block);
        if (dx < 0) {
          goToSlide(block, (cur + 1) % c);
        } else {
          goToSlide(block, (cur - 1 + c) % c);
        }
      },
      { passive: true },
    );
  }

  function initNumberedPointsCarousel(block) {
    const bullets = block.querySelectorAll(".numbered-points__pagination-dot");
    const slides = block.querySelectorAll(".numbered-points__slide--content");
    const count = slides.length;

    if (count <= 1) return;

    block.setAttribute("data-slide-count", String(count));
    block.dataset.currentSlide = getCurrentIndex(block).toString();
    syncContentViewportHeight(block, false);

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        syncContentViewportHeight(block, false);
      });
      slides.forEach((slide) => resizeObserver.observe(slide));
    }

    bullets.forEach((btn) => {
      btn.addEventListener("click", () => {
        const raw = btn.getAttribute("data-slide-to");
        const idx = parseInt(raw, 10);
        if (!Number.isNaN(idx)) goToSlide(block, idx);
      });
    });

    const prev = block.querySelector(".numbered-points__arrow--prev");
    const next = block.querySelector(".numbered-points__arrow--next");

    if (prev) {
      prev.addEventListener("click", () => {
        const c = parseInt(block.getAttribute("data-slide-count"), 10);
        const cur = getCurrentIndex(block);
        goToSlide(block, (cur - 1 + c) % c);
      });
    }

    if (next) {
      next.addEventListener("click", () => {
        const c = parseInt(block.getAttribute("data-slide-count"), 10);
        const cur = getCurrentIndex(block);
        goToSlide(block, (cur + 1) % c);
      });
    }

    block.addEventListener("keydown", (e) => {
      const c = parseInt(block.getAttribute("data-slide-count"), 10);
      if (!Number.isFinite(c) || c < 2) return;

      const currentIdx = getCurrentIndex(block);

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        const n = (currentIdx + 1) % c;
        goToSlide(block, n);
        block.querySelector(`[data-slide-to="${n}"]`)?.focus?.();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        const n = (currentIdx - 1 + c) % c;
        goToSlide(block, n);
        block.querySelector(`[data-slide-to="${n}"]`)?.focus?.();
      }
    });

    initMobileSwipe(block);
  }

  function init() {
    document
      .querySelectorAll("[data-numbered-points-carousel]")
      .forEach(initNumberedPointsCarousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
